import { useEffect, useRef } from "react";
import * as d3 from "d3";

/**
 * Reference layout: goals ×2, insights ×4, actions ×3, habits ×2, milestones ×3
 * Some labels use pill backgrounds (dark translucent capsule).
 */
const GRAPH = {
  nodes: [
    { id: "g1", label: "goals", pill: true, r: 7 },
    { id: "g2", label: "goals", pill: false, r: 6 },
    { id: "i1", label: "insights", pill: true, r: 8 },
    { id: "i2", label: "insights", pill: false, r: 7 },
    { id: "i3", label: "insights", pill: true, r: 7 },
    { id: "i4", label: "insights", pill: false, r: 6 },
    { id: "a1", label: "actions", pill: false, r: 7 },
    { id: "a2", label: "actions", pill: false, r: 6 },
    { id: "a3", label: "actions", pill: true, r: 7 },
    { id: "h1", label: "habits", pill: false, r: 7 },
    { id: "h2", label: "habits", pill: false, r: 6 },
    { id: "m1", label: "milestones", pill: true, r: 8 },
    { id: "m2", label: "milestones", pill: false, r: 7 },
    { id: "m3", label: "milestones", pill: false, r: 7 },
  ],
  links: [
    { source: "g1", target: "i1" },
    { source: "i1", target: "a1" },
    { source: "a1", target: "h1" },
    { source: "h1", target: "m1" },
    { source: "m1", target: "g2" },
    { source: "g2", target: "i2" },
    { source: "i2", target: "a2" },
    { source: "a2", target: "h2" },
    { source: "h2", target: "m2" },
    { source: "m2", target: "i3" },
    { source: "i3", target: "a3" },
    { source: "a3", target: "m3" },
    { source: "m3", target: "g1" },
    { source: "i1", target: "m2" },
    { source: "i1", target: "m3" },
    { source: "a1", target: "i2" },
    { source: "h1", target: "i3" },
    { source: "m1", target: "a3" },
    { source: "i4", target: "m3" },
    { source: "h2", target: "i4" },
    { source: "g1", target: "i4" },
    { source: "a2", target: "i4" },
    { source: "m2", target: "a1" },
  ],
};

function linkPath(d) {
  const sx = d.source.x;
  const sy = d.source.y;
  const tx = d.target.x;
  const ty = d.target.y;
  const mx = (sx + tx) / 2;
  const my = (sy + ty) / 2;
  const dx = tx - sx;
  const dy = ty - sy;
  const len = Math.hypot(dx, dy) || 1;
  const ox = (-dy / len) * 32;
  const oy = (dx / len) * 32;
  return `M${sx},${sy} Q${mx + ox},${my + oy} ${tx},${ty}`;
}

function dragBehavior(simulation) {
  function dragstarted(event) {
    if (!event.active) simulation.alphaTarget(0.3).restart();
    event.subject.fx = event.subject.x;
    event.subject.fy = event.subject.y;
  }
  function dragged(event) {
    event.subject.fx = event.x;
    event.subject.fy = event.y;
  }
  function dragended(event) {
    if (!event.active) simulation.alphaTarget(0);
    event.subject.fx = null;
    event.subject.fy = null;
  }
  return d3
    .drag()
    .on("start", dragstarted)
    .on("drag", dragged)
    .on("end", dragended);
}

export default function SerendipityNetworkGraph({ className = "" }) {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    let simulation = null;
    let resizeTimer;
    let dashTimer = null;

    const build = () => {
      if (simulation) {
        simulation.stop();
        simulation = null;
      }
      if (dashTimer) {
        dashTimer.stop();
        dashTimer = null;
      }
      d3.select(container).selectAll("*").remove();

      const width = Math.max(280, container.clientWidth || 400);
      const height = Math.max(300, Math.min(460, Math.round(width * 0.85)));

      const gradId = `sn-bg-${Math.random().toString(36).slice(2, 9)}`;
      /* Node glow: CSS drop-shadow only — no SVG filter / glowId */

      const nodes = GRAPH.nodes.map((d) => ({ ...d }));
      const links = GRAPH.links.map((d) => ({ ...d }));

      const svg = d3
        .select(container)
        .append("svg")
        .attr("width", width)
        .attr("height", height)
        .attr("class", "block max-h-[460px] w-full select-none")
        .attr("role", "img")
        .attr(
          "aria-label",
          "Interactive network linking goals, insights, actions, habits, and milestones",
        );

      const defs = svg.append("defs");

      defs
        .append("linearGradient")
        .attr("id", gradId)
        .attr("x1", "0%")
        .attr("y1", "0%")
        .attr("x2", "100%")
        .attr("y2", "100%")
        .selectAll("stop")
        .data([
          { offset: "0%", color: "#ecfeff" },
          { offset: "42%", color: "#cffafe" },
          { offset: "100%", color: "#e0f2fe" },
        ])
        .join("stop")
        .attr("offset", (d) => d.offset)
        .attr("stop-color", (d) => d.color);

      defs
        .append("radialGradient")
        .attr("id", `${gradId}-rad`)
        .attr("cx", "50%")
        .attr("cy", "38%")
        .attr("r", "72%")
        .selectAll("stop")
        .data([
          { offset: "0%", color: "rgba(207, 250, 254, 0.95)" },
          { offset: "55%", color: "rgba(224, 242, 254, 0.85)" },
          { offset: "100%", color: "rgba(236, 254, 255, 0.65)" },
        ])
        .join("stop")
        .attr("offset", (d) => d.offset)
        .attr("stop-color", (d) => d.color);

      svg
        .append("rect")
        .attr("width", width)
        .attr("height", height)
        .attr("fill", `url(#${gradId}-rad)`);

      svg
        .append("rect")
        .attr("width", width)
        .attr("height", height)
        .attr("fill", `url(#${gradId})`)
        .attr("opacity", 0.35);

      simulation = d3
        .forceSimulation(nodes)
        .force(
          "link",
          d3.forceLink(links).id((d) => d.id).distance(95).strength(0.65),
        )
        .force("charge", d3.forceManyBody().strength(-220))
        .force("center", d3.forceCenter(width / 2, height / 2))
        .force("collide", d3.forceCollide((d) => (d.r || 8) + 22));

      const linkG = svg.append("g").attr("class", "sn-links");

      const linkBase = linkG
        .selectAll("path.sn-link-base")
        .data(links)
        .join("path")
        .attr("class", "sn-link-base")
        .attr("fill", "none")
        .attr("stroke", "#7dd3fc")
        .attr("stroke-opacity", 0.22)
        .attr("stroke-width", 2.5)
        .attr("stroke-linecap", "round");

      const linkFlow = linkG
        .selectAll("path.sn-link-flow")
        .data(links)
        .join("path")
        .attr("class", "sn-link-flow")
        .attr("fill", "none")
        .attr("stroke", "#06b6d4")
        .attr("stroke-opacity", 0.75)
        .attr("stroke-width", 1.25)
        .attr("stroke-linecap", "round")
        .attr("stroke-dasharray", "6 14")
        .attr("stroke-dashoffset", 0);

      const node = svg
        .append("g")
        .selectAll("g")
        .data(nodes)
        .join("g")
        .call(dragBehavior(simulation));

      node.each(function (d) {
        const g = d3.select(this);
        const label = (d.label || d.id).toLowerCase();
        const r = d.r || 8;
        const pill = d.pill;

        g.append("circle")
          .attr("r", r + 3)
          .attr("fill", "rgba(34, 211, 238, 0.15)")
          .attr("class", "pointer-events-none");

        g.append("circle")
          .attr("r", r)
          .attr("fill", "#06b6d4")
          .attr("stroke", "#fff")
          .attr("stroke-width", 1.5)
          .style("filter", `drop-shadow(0 0 10px rgba(34, 211, 238, 0.85))`)
          .style("cursor", "grab");

        const tx = r + 10;
        const ty = 4;

        if (pill) {
          const tw = Math.max(label.length * 6.2 + 16, 52);
          const th = 20;
          g.append("rect")
            .attr("x", tx - 4)
            .attr("y", ty - th + 6)
            .attr("width", tw)
            .attr("height", th)
            .attr("rx", th / 2)
            .attr("fill", "rgba(15, 23, 42, 0.42)")
            .attr("stroke", "rgba(148, 163, 184, 0.35)")
            .attr("stroke-width", 1)
            .style("pointer-events", "none");
        }

        g.append("text")
          .text(label)
          .attr("x", pill ? tx + 6 : tx)
          .attr("y", ty)
          .attr("fill", pill ? "#f8fafc" : "#0f172a")
          .attr("font-size", pill ? 11 : 11)
          .attr("font-weight", pill ? 600 : 500)
          .attr("font-family", "system-ui, sans-serif")
          .style("text-transform", "lowercase")
          .style("pointer-events", "none");
      });

      let tDash = 0;
      dashTimer = d3.interval(() => {
        tDash -= 0.65;
        linkFlow.attr("stroke-dashoffset", tDash);
      }, 28);

      simulation.on("tick", () => {
        linkBase.attr("d", linkPath);
        linkFlow.attr("d", linkPath);
        node.attr("transform", (d) => `translate(${d.x},${d.y})`);
      });
    };

    build();

    const ro = new ResizeObserver(() => {
      clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(build, 120);
    });
    ro.observe(container);

    return () => {
      clearTimeout(resizeTimer);
      ro.disconnect();
      if (dashTimer) dashTimer.stop();
      if (simulation) simulation.stop();
      d3.select(container).selectAll("*").remove();
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={`relative w-full overflow-hidden rounded-xl ring-1 ring-cyan-200/60 ${className}`}
      style={{
        minHeight: "min(420px, 70vw)",
        background:
          "linear-gradient(145deg, rgba(236,254,255,0.95) 0%, rgba(207,250,254,0.9) 45%, rgba(224,242,254,0.92) 100%)",
      }}
    />
  );
}
